/* eslint-disable max-len */
import { Arr } from '@deqo/foundation';
import { tint } from 'polished';
import React, { useCallback, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled, { css } from 'styled-components';
import { useDraft } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { useLoader } from '../../platform/Hook';
import { Button, Cell, Colors, Grid, Prompt } from '../../ui';
import { LoadingGuard } from '../../ui/LoadingGuard';
import { Roundings } from '../../ui/Standards';
import { TextButton } from '../../ui/TextButton';
import { AppLocation } from '../AppLocation';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { Copy, DisableFrame, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { AccountLocation, AccountLocationSelector } from './AccountLocationSelector';

const XScrollbars = Scrollbars as any;

const LegacyItem = styled.div<{ selected: boolean }>`
    display: flex;
    flex-direction: column;
    gap: .25rem;
    padding: 0.375rem;
    border-radius: ${Roundings.md};
    cursor: pointer;
    user-select: none;
    box-shadow: inset 0 0 0 .0625rem ${Colors.active};

    &:hover {
        box-shadow: inset 0 0 0 .1875rem ${Colors.hover};
    }

    &:active {
        box-shadow: inset 0 0 0 .1875rem ${Colors.active};
    }

    ${p => p.selected && css` 
        box-shadow: inset 0 0 0 .1875rem ${Colors.primary} !important;
    `}
`;

const LegacyThumb = styled.div<{ index?: number, src?: string }>`
    height: 9.6875rem;
    background-color: ${p => tint(0.5, Arr.at(Colors.brand, p.index || 0))};
    background-image: url(${p => p.src || ''});
    background-size: cover;
    border-radius: ${Roundings.sm};
    overflow: hidden;
    border: solid 1px ${Colors.border};
`;

export const importBoardSelectPopup = () => ({ ui: ImportBoardSelectPopup });

export const ImportBoardSelectPopup: LFC<PopupProps> = ({ action, dismiss }) => {

    const api = useResolve(ApiClient);
    const account = useResolve(AccountModel);
    const location = useResolve(AppLocation);
    const initialWorkspace = useMemo(() => {
        const target = location.get('workspace') || 'default';
        const result = account.workspaces.find(x => x.id === target) ?? account.workspaces[0];
        return { workspaceId: result.id } as AccountLocation;
    }, []);

    const [loading, items] = useLoader(async () => await api.exec(v2.boards.importList(account.id)), []);
    const [selection, _patch, _reset, _dirty, setSelection] = useDraft({} as Record<string, boolean>);
    const selectionList = useMemo(() => Object.entries(selection).filter(x => x[1]).map(x => x[0]), [selection]);
    const [workspace, setWorkspace] = useState(initialWorkspace);

    const onSelect = useCallback((id: string) => setSelection(x => ({ ...x, [id]: !x[id] })), []);
    const onSelectAll = useCallback(() => setSelection(Object.fromEntries(items.map(x => [x.id, true]))), [items]);
    const onClearAll = useCallback(() => setSelection({}), [items]);
    const onImport = useCallback(() => void (action({ list: selectionList, ...workspace })), [action, selectionList, workspace]);

    const onPromptAction = useCallback((confirm: boolean) => {
        if (!confirm) dismiss();
    }, [dismiss]);

    return <Prompt onAction={onPromptAction}>
        <Popup w={48} h={55.5}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Header>
                <Popup.Title>Import Boards</Popup.Title>
                <Popup.SubTitle>Bring over your boards from Metro Retro v1</Popup.SubTitle>
            </Popup.Header>
            <Popup.Content>
                <LoadingGuard loading={loading}>
                    <DisableFrame disabled={!items?.length}>
                        <Flexer flex="column" padding="1 0 .25 0" gap=".25">
                            <Copy color={Colors.textLight} flush>Import Into:</Copy>
                            <AccountLocationSelector initial={initialWorkspace} onSelect={setWorkspace} />
                        </Flexer>
                        <Flexer flex="row" justify="space-between" padding="1 0 .25 0">
                            <Copy color={!!selectionList.length ? Colors.primary : Colors.textLight} flush>
                                {selectionList.length} / {items?.length} selected.
                            </Copy>
                            {!selectionList.length && <TextButton onClick={onSelectAll}>Select All</TextButton>}
                            {!!selectionList.length && <TextButton onClick={onClearAll}>Clear Selection</TextButton>}
                        </Flexer>
                    </DisableFrame>
                    <Flexer grow margin="0 -1 0 0">
                        <XScrollbars style={{ height: '100% ' }} >
                            <Flexer padding="0 1 0 0">
                                <Grid columns="1fr 1fr 1fr" columnGap=".5rem" rowGap=".5rem">
                                    {!loading && items.map((x, i) =>
                                        <Cell key={x.id} onClick={() => onSelect(x.id)}>
                                            <LegacyItem selected={selection[x.id]}>
                                                <LegacyThumb index={i} src={x.thumbUrl} />
                                                <Flexer padding=".25">
                                                    <Title flush size="sm" color={Colors.text}>{x.label}</Title>
                                                    <Copy flush size=".75" color={Colors.textLight}>Created {x.createdAt?.toLocaleDateString()}</Copy>
                                                </Flexer>
                                            </LegacyItem>
                                        </Cell>
                                    )}
                                </Grid>
                            </Flexer>
                        </XScrollbars>
                        {!loading && !items?.length &&
                            <Flexer inset flex center pointerOff gap=".5">
                                <Title color={Colors.gray400} flush size="2xl">You&apos;ve imported all your boards!</Title>
                                <Title color={Colors.gray400} flush size="md">There&apos;s nothing left to do here.</Title>
                            </Flexer>
                        }
                    </Flexer>
                </LoadingGuard>
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={!selectionList?.length} variant="primary" onAction={onImport}>
                    Import Boards
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
